<script setup>
import { computed, inject, nextTick, onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import PortalCsatSurvey from './PortalCsatSurvey.vue';
import {
  statusStyle as resolveStatusStyle,
  DEFAULT_ACCENT_RGB,
  PRIORITY_COLORS,
} from './ticketStatusStyles';
import { initials } from './ticketFormatters';

const props = defineProps({
  displayId: { type: [String, Number], required: true },
});

const config = inject('portalTicketsConfig');
const accent = config.accent;
const labels = config.detailLabels;
const ticketsPath = config.ticketsPath;
const messagesUrl = `${ticketsPath}/${props.displayId}/messages`;
const ticketUrl = `${ticketsPath}/${props.displayId}.json`;

const ticket = ref({});
const scrollContainer = ref(null);
const messages = ref([]);
const hasMore = ref(false);
const loading = ref(false);
const notFound = ref(false);

const MESSAGE_TYPE_INCOMING = 0;
const TIME_ZONE = 'America/Sao_Paulo';

const intlLocale = computed(() =>
  String(config.locale || 'pt-BR').replace('_', '-')
);

const hexToRgb = hex => {
  let value = String(hex || '')
    .replace('#', '')
    .trim();
  if (value.length === 3) {
    value = value
      .split('')
      .map(char => char + char)
      .join('');
  }
  if (value.length < 6) return DEFAULT_ACCENT_RGB;
  const channels = [
    Number.parseInt(value.slice(0, 2), 16),
    Number.parseInt(value.slice(2, 4), 16),
    Number.parseInt(value.slice(4, 6), 16),
  ];
  return channels.some(Number.isNaN) ? DEFAULT_ACCENT_RGB : channels;
};

const accentSoft = computed(() => `rgba(${hexToRgb(accent).join(',')}, 0.16)`);

const statusStyle = computed(() => resolveStatusStyle(ticket.value.status));

const readonlyNote = computed(() =>
  ticket.value.status === 'resolved'
    ? labels.readonly_note_closed
    : labels.readonly_note
);

const formatTime = unix =>
  new Date(unix * 1000).toLocaleTimeString(intlLocale.value, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIME_ZONE,
  });

const formatDate = unix =>
  new Date(unix * 1000).toLocaleDateString(intlLocale.value, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: TIME_ZONE,
  });

const messageKind = message => {
  if (message.message_type === MESSAGE_TYPE_INCOMING) return 'customer';
  if (message.sender && message.sender.type === 'user') return 'agent';
  return 'bot';
};

const isCustomer = message => messageKind(message) === 'customer';

const isCsat = message => message.content_type === 'input_csat';

const avatarStyle = message => {
  const kind = messageKind(message);
  if (kind === 'customer') {
    return { backgroundColor: accentSoft.value, color: accent };
  }
  if (kind === 'agent') return { backgroundColor: accent, color: '#fff' };
  return { backgroundColor: '#e3effb' };
};

const avatarInitials = message =>
  initials(message.sender?.name || ticket.value.customer);

const metaLabel = message => {
  const time = formatTime(message.created_at);
  const kind = messageKind(message);
  if (kind === 'customer') return time;
  if (kind === 'agent') return `${message.sender?.name || ''} · ${time}`;
  return `${labels.bot} · ${time}`;
};

const fileName = url => {
  try {
    const path = decodeURIComponent(
      new URL(url, window.location.origin).pathname
    );
    return path.split('/').filter(Boolean).pop() || '';
  } catch (error) {
    return '';
  }
};

const attachmentName = attachment => {
  const fromUrl = fileName(attachment.data_url || attachment.thumb_url || '');
  if (fromUrl) return fromUrl;
  return attachment.extension
    ? `${labels.attachment}.${attachment.extension}`
    : labels.attachment;
};

const formatSize = bytes => {
  if (!bytes) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const priorityColor = computed(
  () => PRIORITY_COLORS[ticket.value.priority] || PRIORITY_COLORS.low
);

const priorityLabel = computed(
  () => labels.priorities?.[ticket.value.priority] || ticket.value.priority
);

const groups = computed(() => {
  const result = [];
  let currentKey = null;
  messages.value.forEach(message => {
    const key = new Date(message.created_at * 1000).toLocaleDateString(
      'en-CA',
      {
        timeZone: TIME_ZONE,
      }
    );
    if (key !== currentKey) {
      result.push({ key, label: formatDate(message.created_at), messages: [] });
      currentKey = key;
    }
    result[result.length - 1].messages.push(message);
  });
  return result;
});

const scrollToBottom = () => {
  const el = scrollContainer.value;
  if (el) el.scrollTop = el.scrollHeight;
};

const loadMessages = async before => {
  if (loading.value) return;
  loading.value = true;
  try {
    const url = before ? `${messagesUrl}?before=${before}` : messagesUrl;
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to load messages');
    const data = await response.json();
    messages.value = before
      ? [...data.messages, ...messages.value]
      : data.messages;
    hasMore.value = data.has_more;
  } catch {
    hasMore.value = false;
  } finally {
    loading.value = false;
  }
};

const onScroll = async () => {
  const el = scrollContainer.value;
  if (!el || !hasMore.value || loading.value || el.scrollTop > 48) return;

  const previousHeight = el.scrollHeight;
  await loadMessages(messages.value[0]?.id);
  await nextTick();
  el.scrollTop = el.scrollHeight - previousHeight;
};

onMounted(async () => {
  try {
    const response = await fetch(ticketUrl, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      notFound.value = true;
      return;
    }
    ticket.value = await response.json();
  } catch (error) {
    notFound.value = true;
    return;
  }
  await loadMessages();
  await nextTick();
  scrollToBottom();
});
</script>

<template>
  <div class="mx-auto w-full max-w-[1180px]">
    <RouterLink
      :to="{ name: 'ticket-list' }"
      class="mb-4 inline-flex w-fit items-center gap-2 text-[13px] font-semibold text-[#8a909a] transition-colors hover:text-[#15181d] dark:text-slate-400 dark:hover:text-slate-100"
    >
      <span class="i-lucide-arrow-left size-4" />
      {{ labels.back }}
    </RouterLink>

    <p
      v-if="notFound"
      class="mt-6 rounded-2xl border border-solid border-[#ececec] bg-white px-[22px] py-6 text-sm font-medium text-[#8a909a] dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400"
    >
      {{ labels.not_found }}
    </p>

    <template v-else-if="ticket.display_id">
      <div
        class="mb-4 rounded-2xl border border-solid border-[#ececec] bg-white px-[22px] py-[18px] dark:border-slate-800 dark:bg-slate-900"
      >
        <div class="font-mono text-xs tracking-wider text-[#a4a9b1]">
          #{{ ticket.display_id }}
        </div>
        <h1
          class="mt-1 text-[21px] font-extrabold tracking-tight text-[#15181d] dark:text-white"
        >
          {{ ticket.subject }}
        </h1>
        <div class="mt-3 flex flex-wrap items-center gap-2">
          <span
            class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold"
            :class="statusStyle.badge"
          >
            <span
              class="size-[7px] rounded-full"
              :style="{ backgroundColor: statusStyle.dot }"
            />
            {{ ticket.status_label }}
          </span>
          <span
            v-for="tag in ticket.tags"
            :key="tag"
            class="rounded-full bg-[#f1f2f4] px-2.5 py-1 text-xs font-semibold text-[#5a616b] dark:bg-slate-800 dark:text-slate-300"
          >
            {{ tag }}
          </span>
        </div>
      </div>

      <div class="flex flex-wrap items-start gap-4">
        <div
          class="flex max-h-[75vh] min-w-[300px] flex-1 basis-[560px] flex-col rounded-2xl border border-solid border-[#ececec] bg-white dark:border-slate-800 dark:bg-slate-900"
        >
          <div
            ref="scrollContainer"
            class="flex min-h-0 flex-1 flex-col gap-[18px] overflow-y-auto px-6 py-[22px]"
            @scroll="onScroll"
          >
            <template v-for="group in groups" :key="group.key">
              <div class="flex justify-center">
                <span
                  class="rounded-full bg-[#eef0f2] px-3 py-1 text-xs font-semibold text-[#7b818b] dark:bg-slate-800 dark:text-slate-400"
                >
                  {{ group.label }}
                </span>
              </div>

              <div
                v-for="message in group.messages"
                :key="message.id"
                class="flex gap-2.5"
                :class="
                  isCustomer(message) ? 'items-end justify-end' : 'items-start'
                "
              >
                <span
                  v-if="!isCustomer(message)"
                  class="flex size-8 flex-none items-center justify-center rounded-full text-[11px] font-bold"
                  :style="avatarStyle(message)"
                >
                  <span
                    v-if="messageKind(message) === 'bot'"
                    class="i-lucide-bot size-[18px] text-[#3b82f6]"
                  />
                  <template v-else>{{ avatarInitials(message) }}</template>
                </span>

                <div
                  class="flex flex-col gap-1.5"
                  :class="[
                    isCustomer(message) ? 'items-end' : '',
                    isCsat(message)
                      ? 'w-full max-w-[440px]'
                      : 'max-w-[80%] sm:max-w-[66%]',
                  ]"
                >
                  <div
                    v-if="message.content"
                    class="whitespace-pre-line px-[15px] py-3 text-sm leading-relaxed text-[#2b2f36]"
                    :class="
                      isCustomer(message)
                        ? 'rounded-[18px_18px_6px_18px]'
                        : 'rounded-[6px_18px_18px_18px] bg-[#f3f4f6] dark:bg-slate-800 dark:text-slate-100'
                    "
                    :style="
                      isCustomer(message)
                        ? { backgroundColor: accentSoft }
                        : undefined
                    "
                  >
                    {{ message.content }}
                  </div>

                  <a
                    v-for="attachment in message.attachments"
                    :key="attachment.id"
                    :href="attachment.data_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="flex items-center gap-2.5 rounded-xl border border-solid border-[#e7e9ed] bg-white px-3 py-2 transition-colors hover:bg-[#fafafa] dark:border-slate-700 dark:bg-slate-800"
                  >
                    <span
                      class="flex size-[30px] flex-none items-center justify-center rounded-lg bg-[#eef0f2] text-[#7b818b] dark:bg-slate-700 dark:text-slate-300"
                    >
                      <span
                        class="size-4"
                        :class="
                          attachment.file_type === 'image'
                            ? 'i-lucide-image'
                            : 'i-lucide-file'
                        "
                      />
                    </span>
                    <span class="min-w-0">
                      <span
                        class="block truncate text-[13px] font-semibold text-[#2b2f36] dark:text-slate-100"
                      >
                        {{ attachmentName(attachment) }}
                      </span>
                      <span
                        v-if="formatSize(attachment.file_size)"
                        class="block text-[11px] text-[#a4a9b1]"
                      >
                        {{ formatSize(attachment.file_size) }}
                      </span>
                    </span>
                  </a>

                  <PortalCsatSurvey
                    v-if="isCsat(message)"
                    :message="message"
                    :accent="accent"
                    :labels="labels.csat || {}"
                    :messages-url="messagesUrl"
                    @submitted="
                      values =>
                        (message.content_attributes = {
                          ...message.content_attributes,
                          submitted_values: values,
                        })
                    "
                  />

                  <div class="text-[11px] text-[#a4a9b1]">
                    {{ metaLabel(message) }}
                  </div>
                </div>

                <span
                  v-if="isCustomer(message)"
                  class="flex size-8 flex-none items-center justify-center rounded-full text-[11px] font-bold"
                  :style="avatarStyle(message)"
                >
                  {{ avatarInitials(message) }}
                </span>
              </div>
            </template>
          </div>

          <div
            class="flex items-center justify-center gap-2 border-t border-solid border-[#eef0f2] px-6 py-3.5 dark:border-slate-800"
          >
            <span class="i-lucide-lock size-[15px] text-[#9aa1ab]" />
            <span class="text-xs font-semibold text-[#9aa1ab]">
              {{ readonlyNote }}
            </span>
          </div>
        </div>

        <div
          class="min-w-[260px] max-w-[320px] flex-1 basis-[280px] rounded-2xl border border-solid border-[#ececec] bg-white px-5 py-[18px] dark:border-slate-800 dark:bg-slate-900"
        >
          <div
            class="mb-4 text-[13px] font-extrabold tracking-wide text-[#15181d] dark:text-white"
          >
            {{ labels.details_title }}
          </div>
          <div class="flex flex-col gap-[15px]">
            <div>
              <div
                class="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-[#a4a9b1]"
              >
                {{ labels.assignee }}
              </div>
              <div class="flex items-center gap-2">
                <span
                  class="flex size-[26px] flex-none items-center justify-center rounded-full text-[10px] font-bold text-white"
                  :style="{
                    backgroundColor: ticket.assignee ? accent : '#cbd2da',
                  }"
                >
                  {{ ticket.assignee ? initials(ticket.assignee) : '—' }}
                </span>
                <div
                  class="text-sm font-semibold text-[#2b2f36] dark:text-slate-200"
                >
                  {{ ticket.assignee || '—' }}
                </div>
              </div>
            </div>

            <div v-if="ticket.customer">
              <div
                class="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-[#a4a9b1]"
              >
                {{ labels.customer }}
              </div>
              <div class="flex items-center gap-2">
                <span
                  class="flex size-[26px] flex-none items-center justify-center rounded-full text-[10px] font-bold"
                  :style="{ backgroundColor: accentSoft, color: accent }"
                >
                  {{ initials(ticket.customer) }}
                </span>
                <div
                  class="text-sm font-semibold text-[#2b2f36] dark:text-slate-200"
                >
                  {{ ticket.customer }}
                </div>
              </div>
            </div>

            <div class="h-px bg-[#eef0f2] dark:bg-slate-800" />

            <div
              v-if="ticket.priority"
              class="flex items-center justify-between"
            >
              <span class="text-[13px] text-[#7b818b] dark:text-slate-400">
                {{ labels.priority }}
              </span>
              <span
                class="inline-flex items-center gap-1.5 text-[13px] font-bold"
                :style="{ color: priorityColor }"
              >
                <span
                  class="size-2 rounded-sm"
                  :style="{ backgroundColor: priorityColor }"
                />
                {{ priorityLabel }}
              </span>
            </div>

            <div
              v-if="ticket.channel"
              class="flex items-center justify-between"
            >
              <span class="text-[13px] text-[#7b818b] dark:text-slate-400">
                {{ labels.channel }}
              </span>
              <span
                class="text-[13px] font-semibold text-[#2b2f36] dark:text-slate-200"
              >
                {{ ticket.channel }}
              </span>
            </div>

            <div class="flex items-center justify-between">
              <span class="text-[13px] text-[#7b818b] dark:text-slate-400">
                {{ labels.opened_at }}
              </span>
              <span
                class="text-[13px] font-semibold text-[#2b2f36] dark:text-slate-200"
              >
                {{ ticket.created_label }}
              </span>
            </div>

            <div class="flex items-center justify-between">
              <span class="text-[13px] text-[#7b818b] dark:text-slate-400">
                {{ labels.updated_at }}
              </span>
              <span
                class="text-[13px] font-semibold text-[#2b2f36] dark:text-slate-200"
              >
                {{ ticket.updated_label }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
